import { useSession } from 'next-auth/react';
import GridDragAndDrop from '../../../../../Misc/GridDragAndDrop/GridDragAndDrop';
import styles from './styles.module.scss';
import { useMemo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faCubesStacked, faDollarSign, faPlus, faSave, faTableList, faTag } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from 'react-number-format';
import errorMsg from '../../../../../Functions/errorMsg';
import successMsg from '../../../../../Functions/successMsg';
import axios from 'axios';
import Resizer from 'react-image-file-resizer';
import { BounceLoader } from 'react-spinners';

// Function to resize image and convert it to base64
const resizeFile = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      1500,
      1500,
      'PNG',
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64'
    );
  });

export default function CreateProduct({ close }) {
  const { data: session, status } = useSession();

  const [pendingRequest, setPendingRequest] = useState(false);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productQuantity, setProductQuantity] = useState('');
  const [uploads, setUploads] = useState([]);
  const [isCreateEnabled, setIsCreateEnabled] = useState(false);
  const [isSaveDraftEnabled, setIsSaveDraftEnabled] = useState(false);

  useEffect(() => {
    const isFormValid = productName.length >= 5 &&
      productName.length <= 42 &&
      productDescription.length >= 24 &&
      productDescription.length <= 1000 &&
      productPrice > 0 &&
      productCategory.length >= 4 &&
      productCategory.length <= 50 &&
      productQuantity > 0 &&
      uploads.length > 0;
      
    setIsCreateEnabled(isFormValid);

    const isDraftEnabled = productName.length > 0 ||
      productDescription.length > 0 ||
      productPrice > 0 ||
      productCategory.length > 0 ||
      productQuantity > 0 ||
      uploads.length > 0;

    setIsSaveDraftEnabled(isDraftEnabled);
  }, [productName, productDescription, productPrice, productCategory, productQuantity, uploads]);

  // Handlers for Input Changes
  function handleProductNameChange(e) {
    let value = e.target.value;
    value = value.replace(/[^a-zA-Z0-9-:& ]/g, ''); // Allow specific characters
    value = value.replace(/\s{2,}/g, ' '); // Replace multiple spaces with a single space
    setProductName(value);
  }

  function handleProductDescriptionChange(e) {
    const formattedDescription = e.target.value.replace(/[^a-zA-Z0-9!@#$%&()/.,"':;[\]~+=-_ ]/g, '');
    setProductDescription(formattedDescription);
  }

  function handleCategoryChange(e) {
    let value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
    value = value.replace(/\b\w/g, (char) => char.toUpperCase());
    setProductCategory(value);
  }

  // Function to prepare images for upload
  async function prepareImagesForUpload(images) {
    return Promise.all(
      images.map(async (image, index) => {
        if (image.url.startsWith('blob:')) {
          const resizedImage = await resizeFile(image.imageFile);
          return { position: index + 1, url: resizedImage };
        }
        return { position: index + 1, url: image.url };
      })
    );
  }

  async function postNewProduct() {
    if (!isCreateEnabled || pendingRequest) return;

    setPendingRequest(true); // Show loading spinner

    try {
      const preparedImages = await prepareImagesForUpload(uploads);

      const productPayload = {
        productName,
        productDescription,
        productPrice,
        productCategory,
        productQuantity,
        images: preparedImages,
        status: 'Active' // Set status as Active when publishing
      };

      const result = await axios.post(`${window.location.origin}/api/vendor/manage/products/saveProduct`, {
        productData: productPayload
      });

      if (result && result.status === 200) {
        successMsg('Product has been successfully published.');
        close();
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setPendingRequest(false); // Hide loading spinner
    }
  }

  async function saveDraftProduct() {
    if (!isSaveDraftEnabled || pendingRequest) return;

    setPendingRequest(true); // Show loading spinner

    try {
      const preparedImages = await prepareImagesForUpload(uploads);

      const productPayload = {
        productName,
        productDescription,
        productPrice,
        productCategory,
        productQuantity,
        images: preparedImages,
        status: 'Draft' // Set status as Draft when saving
      };

      const result = await axios.post(`${window.location.origin}/api/vendor/manage/products/saveProduct`, {
        productData: productPayload
      });

      if (result && result.status === 200) {
        successMsg("Product was successfully saved as draft.");
        close();
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setPendingRequest(false); // Hide loading spinner
    }
  }

  const memoizeImageGrid = useMemo(() => (
    <GridDragAndDrop 
      initialImages={uploads} 
      pendingRequest={pendingRequest} 
      receiveUploadData={(newUploads) => setUploads(newUploads)}  
    />
  ), [pendingRequest]);

  if (pendingRequest) {
    return (
      <section id={styles.loading}>
        <BounceLoader size={75} speedMultiplier={2} color="#154734" />
      </section>
    );
  }

  return (
    <section id={styles.createProductWrapper}>
      <div className={styles.detailsContainer}>
        <h1>Add Product <FontAwesomeIcon icon={faBox} id={styles.icon} /></h1>

        <div className={styles.basicInfoCard}>
          <h1>Name</h1>
          <input type='text' minLength={5} maxLength={42} value={productName} onChange={handleProductNameChange} />
        </div>

        <div className={styles.basicInfoCard}>
          <h1>Description</h1>
          <textarea type='text' minLength={24} maxLength={1000} value={productDescription} onChange={handleProductDescriptionChange} />
        </div>

        <section className={styles.basicInfoContainer}>
          <div>
            <h1>Price <FontAwesomeIcon icon={faTag} id={styles.icon} /></h1>
            <NumberFormat onValueChange={(values) => setProductPrice(parseFloat(values.value))} isAllowed={priceValueCap} placeholder="$14.99" decimalScale={2} thousandSeparator={true} prefix={'$'} value={productPrice} />
          </div>
          <div>
            <h1>Category <FontAwesomeIcon icon={faTableList} id={styles.icon} /></h1>
            <input type='text' placeholder='Product Category' onChange={handleCategoryChange} value={productCategory} />
          </div>
          <div>
            <h1>Stock <FontAwesomeIcon icon={faCubesStacked} id={styles.icon} /></h1>
            <NumberFormat onValueChange={(values) => setProductQuantity(parseInt(values.value))} isAllowed={quantityValueCap} placeholder="42" decimalSeparator={false} thousandSeparator={true} suffix=' / qty' value={productQuantity} />
          </div>
        </section>

        <div className={styles.actionBar}>
          <button disabled={!isCreateEnabled} onClick={postNewProduct}>Publish <FontAwesomeIcon icon={faPlus} /></button>
          <button disabled={!isSaveDraftEnabled} onClick={saveDraftProduct}>Save <FontAwesomeIcon icon={faSave} /></button>
          <button onClick={close}>Cancel</button>
        </div>
      </div>

      <div className={styles.uploadContainer}>
        <section>{memoizeImageGrid}</section>
        <section>
          <h1>Product Images</h1>
          <h2>{uploads.length} / 12 Slots</h2>
        </section>
      </div>
    </section>
  );
}

/* Misc Functions */

const priceValueCap = (inputObj) => {
  const { value } = inputObj;
  return value <= 1000000000;
};

const quantityValueCap = (inputObj) => {
  const { value } = inputObj;
  return value <= 1000000;
};
